/**
 * Local Project Repository
 *
 * Implements IProjectRepository using localStorage.
 * Enforces one-project limit for free tier.
 * Maps local storage shapes to domain types.
 */

import type {
  LocalProject,
  LocalChapter,
  ProjectRecord,
  ChapterRecord,
  ProjectWithChapters,
  ProjectMode,
} from '@/types/writingStudio'
import type { RepositoryResult } from '@/types/domain'
import type { IProjectRepository, ProjectUpdates, ChapterUpdates } from './projectTypes'
import {
  calculateWordCount,
  generatePlaintextPreview,
  getRandomProjectEmoji,
} from '@/types/writingStudio'
import {
  getLocalProjects,
  getLocalProject,
  saveLocalProject,
  deleteLocalProject,
  getLocalProjectCount,
} from '@/services/localProjectStore'

/**
 * Current child ID being operated on (set by context)
 * This is used to validate access in operations that only receive projectId
 */
let currentChildId: string | null = null

/**
 * Sets the current child context for access validation
 */
export function setCurrentProjectChild(childId: string | null): void {
  currentChildId = childId
}

/**
 * Converts local storage project to unified ProjectRecord format
 */
function toProjectRecord(local: LocalProject): ProjectRecord {
  return {
    id: local.id,
    childId: local.childId,
    title: local.title,
    coverEmoji: local.coverEmoji,
    mode: local.mode,
    status: local.status,
    wordCount: local.wordCount,
    activeChapterId: local.activeChapterId,
    createdAt: local.createdAt,
    updatedAt: local.updatedAt,
    lastExportedAt: local.lastExportedAt ?? null,
  }
}

/**
 * Converts local storage chapter to unified ChapterRecord format
 */
function toChapterRecord(local: LocalChapter, projectId: string): ChapterRecord {
  return {
    id: local.id,
    projectId,
    title: local.title,
    order: local.order,
    summary: local.summary,
    content: local.content,
    plaintextPreview: local.plaintextPreview,
    wordCount: local.wordCount,
    createdAt: local.createdAt,
    updatedAt: local.updatedAt,
  }
}

/**
 * Creates a new chapter with default values
 */
function createDefaultChapter(order: number, title?: string): LocalChapter {
  const now = new Date().toISOString()
  return {
    id: crypto.randomUUID(),
    title: title ?? `Chapter ${order}`,
    order,
    summary: null,
    content: '',
    plaintextPreview: '',
    wordCount: 0,
    createdAt: now,
    updatedAt: now,
  }
}

/**
 * Local Project Repository implementation
 */
export const localProjectRepository: IProjectRepository = {
  async listProjects(childId: string): Promise<RepositoryResult<ProjectRecord[]>> {
    try {
      const projects = getLocalProjects(childId)
      // Sort by updatedAt descending (most recently edited first)
      const sorted = projects
        .map((p) => toProjectRecord(p))
        .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
      return {
        success: true,
        data: sorted,
      }
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Failed to list projects',
        code: 'UNKNOWN',
      }
    }
  },

  async createProject(
    childId: string,
    title: string = 'Untitled Project',
    mode: ProjectMode = 'write'
  ): Promise<RepositoryResult<ProjectWithChapters>> {
    try {
      // Enforce one-project limit
      const count = getLocalProjectCount(childId)
      if (count >= 1) {
        return {
          success: false,
          error: 'Free tier is limited to one project. Export or archive to create more.',
          code: 'LIMIT_REACHED',
        }
      }

      const now = new Date().toISOString()
      const chapter = createDefaultChapter(1)

      const newProject: LocalProject = {
        id: crypto.randomUUID(),
        childId,
        title,
        coverEmoji: getRandomProjectEmoji(),
        mode,
        status: 'draft',
        wordCount: 0,
        activeChapterId: chapter.id,
        createdAt: now,
        updatedAt: now,
        lastExportedAt: null,
        chapters: [chapter],
        notes: '',
      }

      const saved = saveLocalProject(childId, newProject)
      if (!saved) {
        return {
          success: false,
          error: 'Failed to save project to local storage',
          code: 'UNKNOWN',
        }
      }

      return {
        success: true,
        data: {
          project: toProjectRecord(newProject),
          chapters: [toChapterRecord(chapter, newProject.id)],
          notes: '',
        },
      }
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Failed to create project',
        code: 'UNKNOWN',
      }
    }
  },

  async canCreateProject(childId: string): Promise<boolean> {
    const count = getLocalProjectCount(childId)
    // Can create if no projects exist (free tier: 1 project limit)
    return count === 0
  },

  async getProjectCount(childId: string): Promise<number> {
    return getLocalProjectCount(childId)
  },

  async getProject(projectId: string): Promise<RepositoryResult<ProjectRecord | null>> {
    try {
      if (!currentChildId) {
        return { success: false, error: 'No active child context', code: 'VALIDATION_ERROR' }
      }

      const project = getLocalProject(currentChildId, projectId)
      if (!project) {
        return { success: true, data: null }
      }

      return { success: true, data: toProjectRecord(project) }
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Failed to get project',
        code: 'UNKNOWN',
      }
    }
  },

  async getProjectWithChapters(projectId: string): Promise<RepositoryResult<ProjectWithChapters | null>> {
    try {
      if (!currentChildId) {
        return { success: false, error: 'No active child context', code: 'VALIDATION_ERROR' }
      }

      const project = getLocalProject(currentChildId, projectId)
      if (!project) {
        return { success: true, data: null }
      }

      return {
        success: true,
        data: {
          project: toProjectRecord(project),
          chapters: project.chapters.map((c) => toChapterRecord(c, projectId)),
          notes: project.notes,
        },
      }
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Failed to get project with chapters',
        code: 'UNKNOWN',
      }
    }
  },

  async updateProject(projectId: string, updates: ProjectUpdates): Promise<RepositoryResult<ProjectRecord>> {
    try {
      if (!currentChildId) {
        return { success: false, error: 'No active child context', code: 'VALIDATION_ERROR' }
      }

      const project = getLocalProject(currentChildId, projectId)
      if (!project) {
        return { success: false, error: 'Project not found', code: 'NOT_FOUND' }
      }

      // Apply updates
      if (updates.title !== undefined) project.title = updates.title
      if (updates.coverEmoji !== undefined) project.coverEmoji = updates.coverEmoji
      if (updates.status !== undefined) project.status = updates.status
      if (updates.activeChapterId !== undefined) project.activeChapterId = updates.activeChapterId
      project.updatedAt = new Date().toISOString()

      const saved = saveLocalProject(currentChildId, project)
      if (!saved) {
        return { success: false, error: 'Failed to save project', code: 'UNKNOWN' }
      }

      return { success: true, data: toProjectRecord(project) }
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Failed to update project',
        code: 'UNKNOWN',
      }
    }
  },

  async deleteProject(projectId: string): Promise<RepositoryResult<void>> {
    try {
      if (!currentChildId) {
        return { success: false, error: 'No active child context', code: 'VALIDATION_ERROR' }
      }

      const project = getLocalProject(currentChildId, projectId)
      if (!project) {
        return { success: false, error: 'Project not found', code: 'NOT_FOUND' }
      }

      deleteLocalProject(currentChildId, projectId)
      return { success: true, data: undefined }
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Failed to delete project',
        code: 'UNKNOWN',
      }
    }
  },

  // Chapter operations

  async createChapter(projectId: string, title?: string): Promise<RepositoryResult<ChapterRecord>> {
    try {
      if (!currentChildId) {
        return { success: false, error: 'No active child context', code: 'VALIDATION_ERROR' }
      }

      const project = getLocalProject(currentChildId, projectId)
      if (!project) {
        return { success: false, error: 'Project not found', code: 'NOT_FOUND' }
      }

      const nextOrder = Math.max(...project.chapters.map((c) => c.order), 0) + 1
      const chapter = createDefaultChapter(nextOrder, title)
      project.chapters.push(chapter)
      project.updatedAt = new Date().toISOString()

      const saved = saveLocalProject(currentChildId, project)
      if (!saved) {
        return { success: false, error: 'Failed to save chapter', code: 'UNKNOWN' }
      }

      return { success: true, data: toChapterRecord(chapter, projectId) }
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Failed to create chapter',
        code: 'UNKNOWN',
      }
    }
  },

  async updateChapter(chapterId: string, updates: ChapterUpdates): Promise<RepositoryResult<ChapterRecord>> {
    try {
      if (!currentChildId) {
        return { success: false, error: 'No active child context', code: 'VALIDATION_ERROR' }
      }

      // Find project containing this chapter
      const projects = getLocalProjects(currentChildId)
      let foundProject: LocalProject | null = null
      let foundChapter: LocalChapter | null = null

      for (const project of projects) {
        const chapter = project.chapters.find((c) => c.id === chapterId)
        if (chapter) {
          foundProject = project
          foundChapter = chapter
          break
        }
      }

      if (!foundProject || !foundChapter) {
        return { success: false, error: 'Chapter not found', code: 'NOT_FOUND' }
      }

      // Check for collision if expectedUpdatedAt is provided
      if (updates.expectedUpdatedAt && foundChapter.updatedAt !== updates.expectedUpdatedAt) {
        return {
          success: false,
          error: 'Content was modified elsewhere. Please refresh to see the latest version.',
          code: 'CONFLICT',
        }
      }

      // Apply updates
      if (updates.title !== undefined) foundChapter.title = updates.title
      if (updates.summary !== undefined) foundChapter.summary = updates.summary
      if (updates.order !== undefined) foundChapter.order = updates.order
      if (updates.content !== undefined) {
        foundChapter.content = updates.content
        foundChapter.wordCount = calculateWordCount(updates.content)
        foundChapter.plaintextPreview = generatePlaintextPreview(updates.content)
      }
      foundChapter.updatedAt = new Date().toISOString()

      // Recalculate project word count
      foundProject.wordCount = foundProject.chapters.reduce((sum, c) => sum + c.wordCount, 0)
      foundProject.updatedAt = foundChapter.updatedAt

      const saved = saveLocalProject(currentChildId, foundProject)
      if (!saved) {
        return { success: false, error: 'Failed to save chapter', code: 'UNKNOWN' }
      }

      return { success: true, data: toChapterRecord(foundChapter, foundProject.id) }
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Failed to update chapter',
        code: 'UNKNOWN',
      }
    }
  },

  async deleteChapter(chapterId: string): Promise<RepositoryResult<void>> {
    try {
      if (!currentChildId) {
        return { success: false, error: 'No active child context', code: 'VALIDATION_ERROR' }
      }

      // Find project containing this chapter
      const projects = getLocalProjects(currentChildId)
      let foundProject: LocalProject | null = null

      for (const project of projects) {
        const chapterIndex = project.chapters.findIndex((c) => c.id === chapterId)
        if (chapterIndex >= 0) {
          foundProject = project
          // Don't allow deleting the last chapter
          if (project.chapters.length === 1) {
            return { success: false, error: 'Cannot delete the only chapter', code: 'VALIDATION_ERROR' }
          }
          project.chapters.splice(chapterIndex, 1)
          // Update active chapter if needed
          if (project.activeChapterId === chapterId) {
            project.activeChapterId = project.chapters[0].id
          }
          break
        }
      }

      if (!foundProject) {
        return { success: false, error: 'Chapter not found', code: 'NOT_FOUND' }
      }

      // Recalculate project word count
      foundProject.wordCount = foundProject.chapters.reduce((sum, c) => sum + c.wordCount, 0)
      foundProject.updatedAt = new Date().toISOString()

      const saved = saveLocalProject(currentChildId, foundProject)
      if (!saved) {
        return { success: false, error: 'Failed to delete chapter', code: 'UNKNOWN' }
      }

      return { success: true, data: undefined }
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Failed to delete chapter',
        code: 'UNKNOWN',
      }
    }
  },

  async reorderChapters(projectId: string, chapterIds: string[]): Promise<RepositoryResult<ChapterRecord[]>> {
    try {
      if (!currentChildId) {
        return { success: false, error: 'No active child context', code: 'VALIDATION_ERROR' }
      }

      const project = getLocalProject(currentChildId, projectId)
      if (!project) {
        return { success: false, error: 'Project not found', code: 'NOT_FOUND' }
      }

      // Validate all chapters are accounted for (prevents silent drops)
      if (chapterIds.length !== project.chapters.length) {
        return {
          success: false,
          error: 'Chapter count mismatch during reorder',
          code: 'VALIDATION_ERROR',
        }
      }

      // Reorder chapters based on provided IDs
      const reorderedChapters: LocalChapter[] = []
      for (let i = 0; i < chapterIds.length; i++) {
        const chapter = project.chapters.find((c) => c.id === chapterIds[i])
        if (chapter) {
          chapter.order = i + 1
          reorderedChapters.push(chapter)
        }
      }

      // Double-check we didn't lose any chapters
      if (reorderedChapters.length !== project.chapters.length) {
        return {
          success: false,
          error: 'Invalid chapter IDs in reorder request',
          code: 'VALIDATION_ERROR',
        }
      }

      project.chapters = reorderedChapters
      project.updatedAt = new Date().toISOString()

      const saved = saveLocalProject(currentChildId, project)
      if (!saved) {
        return { success: false, error: 'Failed to reorder chapters', code: 'UNKNOWN' }
      }

      return {
        success: true,
        data: reorderedChapters.map((c) => toChapterRecord(c, projectId)),
      }
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Failed to reorder chapters',
        code: 'UNKNOWN',
      }
    }
  },

  // Notes operations

  async getNotes(projectId: string): Promise<RepositoryResult<string>> {
    try {
      if (!currentChildId) {
        return { success: false, error: 'No active child context', code: 'VALIDATION_ERROR' }
      }

      const project = getLocalProject(currentChildId, projectId)
      if (!project) {
        return { success: false, error: 'Project not found', code: 'NOT_FOUND' }
      }

      return { success: true, data: project.notes }
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Failed to get notes',
        code: 'UNKNOWN',
      }
    }
  },

  async updateNotes(projectId: string, content: string): Promise<RepositoryResult<string>> {
    try {
      if (!currentChildId) {
        return { success: false, error: 'No active child context', code: 'VALIDATION_ERROR' }
      }

      const project = getLocalProject(currentChildId, projectId)
      if (!project) {
        return { success: false, error: 'Project not found', code: 'NOT_FOUND' }
      }

      project.notes = content
      project.updatedAt = new Date().toISOString()

      const saved = saveLocalProject(currentChildId, project)
      if (!saved) {
        return { success: false, error: 'Failed to save notes', code: 'UNKNOWN' }
      }

      return { success: true, data: content }
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Failed to update notes',
        code: 'UNKNOWN',
      }
    }
  },

  async recordExport(projectId: string): Promise<RepositoryResult<void>> {
    try {
      if (!currentChildId) {
        return { success: false, error: 'No active child context', code: 'VALIDATION_ERROR' }
      }

      const project = getLocalProject(currentChildId, projectId)
      if (!project) {
        return { success: false, error: 'Project not found', code: 'NOT_FOUND' }
      }

      project.lastExportedAt = new Date().toISOString()

      const saved = saveLocalProject(currentChildId, project)
      if (!saved) {
        return { success: false, error: 'Failed to record export', code: 'UNKNOWN' }
      }

      return { success: true, data: undefined }
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Failed to record export',
        code: 'UNKNOWN',
      }
    }
  },
}
